import React, { createContext, useContext, useCallback, useMemo } from "react";
import useCaminoStore from "stores/caminoStore";

interface FeatureContextValue {
  isEnabled: (feature: string) => boolean;
  enabledFeatures: string[];
}

const FeatureContext = createContext<FeatureContextValue | undefined>(undefined);

export const FeatureProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enabledFeatures = useCaminoStore((state) => state.enabledFeatures);
  const featuresSet = new Set<string>(enabledFeatures);

  // Create a memoized version of isEnabled that updates when features change
  const isEnabled = useCallback((feature: string) => {
    return featuresSet.has(feature);
  }, [featuresSet]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isEnabled,
    enabledFeatures,
  }), [isEnabled, enabledFeatures]);

  return (
    <FeatureContext.Provider value={contextValue}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatureContext = () => {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error("useFeatureContext must be used within FeatureProvider");
  }
  return context;
};
